import {
  Typography,
  Table,
  TableCell,
  TableHead,
  TableRow,
  TableBody,
  makeStyles,
} from '@material-ui/core'
import Alert from '@material-ui/lab/Alert'
import PropTypes from 'prop-types'
import { useQuery } from 'react-query'
import React from 'react'
import { useTranslate } from 'react-admin'
import ConditionalRender from '../../components/ConditionalRender/ConditionalRender'
import COLORS from '../../utils/constants/colors'
import QueryKeys from '../../utils/constants/queryKeys'
import ContainerPadding from '../../components/containerPadding/containerPadding'
import LoaderDots from '../../components/loaders/LoaderDots/LoaderDots'
import ConnectionErrorPage from '../ErrorInConnection/ConnectionErrorPage'
import mainSubscriptionTabStyles from './MainSubscriptionTab/mainSubscriptionTab.styles'
import dateFormatter from '../../utils/helpers/dateHelpers/dateFormatter'
import getBillingSummary from '../../Apis/subscriptions/getBillingSummary'

const useBillingSummaryStyles = makeStyles({
  table: {
    '& .MuiTableCell-root': {
      padding: '2px 10px',
      fontSize: '1rem',
    },
    '& .MuiTableRow-root': {
      height: '24px',
    },
  },
})

const BillingSummary = ({ nextBillingDate, orgId, action }) => {
  const translate = useTranslate()
  const useStyles = makeStyles(mainSubscriptionTabStyles)
  const billingSummaryClasses = useBillingSummaryStyles()
  const classes = useStyles()

  const {
    data: billingSummary,
    isSummaryLoading,
    errorSummary,
  } = useQuery([QueryKeys.GET_BILLING_SUMMARY, orgId], () => getBillingSummary({ orgId }), {
    enabled: Boolean(orgId),
  })

  const billingDate = nextBillingDate ? dateFormatter(nextBillingDate) : null
  const hasOutstanding = billingSummary?.lineItems.some((item) => item.isOutstanding)

  if (errorSummary || !orgId) {
    return <ConnectionErrorPage />
  }

  return (
    <>
      {hasOutstanding && (
        <Alert severity='warning' style={{ marginBottom: '10px' }}>
          {translate('ra.text.outstandingChargesDescription')}
        </Alert>
      )}
      {isSummaryLoading ? (
        <ContainerPadding paddingValue='45%'>
          <LoaderDots
            style={{
              fill: COLORS.orange,
              width: 70,
              height: 70,
            }}
          />
        </ContainerPadding>
      ) : (
        <Table className={`${classes.table} ${billingSummaryClasses.table}`}>
          <TableHead>
            <ConditionalRender
              renderValue={billingSummary && billingSummary?.lineItems.length >= 0}
            >
              <TableRow style={{ backgroundColor: COLORS.opacBlue }}>
                <TableCell></TableCell>
                <TableCell colSpan={2} style={{ textAlign: 'center' }}>
                  {translate('ra.text.addonDes')}
                </TableCell>
                <TableCell style={{ textAlign: 'right' }}>
                  Amount
                </TableCell>
              </TableRow>
            </ConditionalRender>
          </TableHead>
          <TableBody>
            {billingSummary?.lineItems.map((item, idx) => (
              <TableRow
                key={item.isOutstanding ? `outstanding-${idx}` : item.name}
                style={item.isOutstanding ? { backgroundColor: '#fff8e1' } : {}}
              >
                <TableCell style={{ border: 'none', ...(item.isOutstanding && { color: '#e65100' }) }}>
                  {item.name}
                  {item.isOutstanding && ` (${translate('ra.text.outstanding')})`}
                </TableCell>
                <TableCell
                  colSpan={2}
                  style={{ textAlign: 'center', border: 'none', ...(item.isOutstanding && { color: '#e65100' }) }}
                >
                  {item.description}
                </TableCell>
                <TableCell
                  style={{ textAlign: 'right', border: 'none', ...(item.isOutstanding && { color: '#e65100' }) }}
                >
                  {item.grossTotalAmount ? `€${item.grossTotalAmount?.toFixed(2)}` : '-'}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={3} style={{ borderTop: '1px solid #d3d3d3' }}>
                {billingDate && `${translate('ra.text.nextBillingDate')}: ${billingDate}`}
              </TableCell>
              <TableCell
                colSpan={1}
                style={{ textAlign: 'right', borderTop: '1px solid #d3d3d3' }}
              >
                <Typography style={{ fontWeight: 'bold' }}>
                  €{billingSummary?.grossTotalAmount.toFixed(2)}
                </Typography>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      )}
      {action && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
          {action}
        </div>
      )}
    </>
  )
}

BillingSummary.propTypes = {
  nextBillingDate: PropTypes.string,
  orgId: PropTypes.string,
  action: PropTypes.node,
}

export default BillingSummary
